import React from 'react'
import PropTypes from 'prop-types'
import { Button, Tooltip, makeStyles } from '@fluentui/react-components'
import {
  Bookmark24Regular,
  Bookmark24Filled,
  ArrowCounterclockwise24Regular
} from '@fluentui/react-icons'

/**
 * Defines the Fluent UI styles for the BookmarkAction component.
 * Utilizes CSS-in-JS to create scoped class names for layout, borders,
 * and custom icon coloring.
 */
const useStyles = makeStyles({
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '12px',
    paddingTop: '8px',
    borderTop: '1px solid #bcb8b8',
    justifyContent: 'flex-end'
  },
  bookmarkedIcon: {
    color: '#000'
  }
})

/**
 * BookmarkAction Component
 *
 * Renders a set of action buttons attached to a user's message bubble.
 * Features include:
 * 1. A Bookmark toggle button to save/remove a question to the user's Prompt Library.
 * 2. A conditional Retry button that appears if the request failed due to specific
 *    server/timeout errors (429, 499, 503, 504) AND it is the latest message.
 *
 * @param {Object} props - The component properties.
 * @param {boolean} props.isBookmarked - Indicates if the current message is already bookmarked.
 * @param {Function} props.onToggle - Callback function triggered when the bookmark button is clicked.
 * @param {boolean} props.isGloballyLoading - Indicates if the application is in a loading state, disabling interactions.
 * @param {number} props.errorStatus - The HTTP status code returned if the message request failed.
 * @param {Function} props.onRetry - Callback function triggered when the retry button is clicked.
 * @param {boolean} props.isLatest - Indicates if this message is the most recent user message in the chat.
 */
const BookmarkAction = ({
  isBookmarked,
  onToggle,
  isGloballyLoading,
  errorStatus,
  onRetry,
  isLatest
}) => {
  const styles = useStyles()

  /**
  * Derived State: Determines if the Retry button should be visible.
  * It only shows for the most recent message (`isLatest`) AND if the error status
  * matches specific network/timeout issues:
  * - 429: Too Many Requests
  * - 499: Client Closed Request (Custom Nginx/Cloud timeout)
  * - 503: Service Unavailable
  * - 504: Gateway Timeout
  */

  const showRetry =
    (errorStatus === 429 ||
      errorStatus === 503 ||
      errorStatus === 504 ||
      errorStatus === 499) &&
    isLatest

  return (
    <div className={styles.actionsContainer}>
      {/* RETRY BUTTON */}
      {showRetry &&
        <Tooltip content='Retry request' relationship='label'>
          <Button
            appearance='subtle'
            icon={
              <ArrowCounterclockwise24Regular className={styles.retryButton} />
            }
            onClick={onRetry}
            disabled={isGloballyLoading}
            aria-label='Retry request'
          />
        </Tooltip>}
      {/* BOOKMARK BUTTON */}
      <Tooltip
        content={isBookmarked ? 'Remove bookmark' : 'Bookmark this question'}
        relationship='label'
      >
        <Button
          appearance='subtle'
          icon={
            isBookmarked
              ? <Bookmark24Filled className={styles.bookmarkedIcon} />
              : <Bookmark24Regular />
          }
          onClick={onToggle}
          disabled={isGloballyLoading}
          style={{ color: 'black' }}
        />
      </Tooltip>
    </div>
  )
}

BookmarkAction.propTypes = {
  isBookmarked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  isGloballyLoading: PropTypes.bool,
  errorStatus: PropTypes.number,
  onRetry: PropTypes.func
}

export default BookmarkAction
